/**
 * Chain-aware NFT API selector.
 *
 * Components import `mintNFT` / `listNFTs` from here instead of `./nft`
 * directly. At build time, the `PUBLIC_CHAIN_PROVIDER` env var picks the
 * implementation:
 *
 *   - "cardano" → uses `nft-cardano.ts` (CIP-68 + Blockfrost backend)
 *   - anything else (default) → uses `nft.ts` (Solana + Metaplex)
 *
 * This file deliberately re-exports both the function and the response
 * types so consumers don't need to know which chain is active. Field names
 * are normalised: `txHash` is the on-chain identifier on either chain.
 */

import {
  mintNFT as mintNFTSolana,
  listNFTs as listNFTsSolana,
  type MintNFTRequest,
  type MintNFTResponse as MintNFTResponseSolana,
  type NFTItem as NFTItemSolana,
} from './nft';
import {
  mintNFTCardano,
  listNFTsCardano,
  type MintNFTCardanoResponse,
  type NFTCardanoItem,
} from './nft-cardano';

const useCardano = import.meta.env.PUBLIC_CHAIN_PROVIDER === 'cardano';

/** Unified mint response — `txHash` and `assetId` are populated for both chains. */
export interface UnifiedMintResponse {
  id: string;
  txHash: string;
  assetId: string;
  name: string;
  network: string;
  explorerUrl: string;
  message: string;
}

/** Unified NFT list item. */
export interface UnifiedNFTItem {
  id: string;
  petId: string;
  txHash: string | null;
  assetId: string | null;
  name: string;
  imageUrl: string | null;
  artworkType: string;
  frame: string | null;
  background: string | null;
  caption: string | null;
  seasonTheme: string | null;
  network: string;
  hidden: boolean;
  createdAt: string;
  explorerUrl: string | null;
}

export type { MintNFTRequest };

export async function mintNFT(req: MintNFTRequest): Promise<UnifiedMintResponse> {
  if (useCardano) {
    const r: MintNFTCardanoResponse = await mintNFTCardano(req);
    return {
      id: r.id,
      txHash: r.cardanoTxHash,
      assetId: r.cardanoAssetId,
      name: r.name,
      network: r.network,
      explorerUrl: r.explorerUrl,
      message: r.message,
    };
  }
  const r: MintNFTResponseSolana = await mintNFTSolana(req);
  return {
    id: r.id,
    txHash: r.txSignature,
    assetId: r.nftMintAddress,
    name: r.name,
    network: r.network,
    explorerUrl: r.explorerUrl,
    message: r.message,
  };
}

export async function listNFTs(
  petId?: string,
): Promise<{ nfts: UnifiedNFTItem[]; total: number }> {
  if (useCardano) {
    const r = await listNFTsCardano(petId);
    return {
      nfts: r.nfts.map((n: NFTCardanoItem) => ({
        id: n.id,
        petId: n.petId,
        txHash: n.cardanoTxHash,
        assetId: n.cardanoAssetId,
        name: n.name,
        imageUrl: n.imageUrl,
        artworkType: n.artworkType,
        frame: n.frame,
        background: n.background,
        caption: n.caption,
        seasonTheme: n.seasonTheme,
        network: n.network,
        hidden: n.hidden,
        createdAt: n.createdAt,
        explorerUrl: n.explorerUrl,
      })),
      total: r.total,
    };
  }
  const r = await listNFTsSolana(petId);
  return {
    nfts: r.nfts.map((n: NFTItemSolana) => ({
      id: n.id,
      petId: n.petId,
      txHash: n.txSignature,
      assetId: n.nftMintAddress,
      name: n.name,
      imageUrl: n.imageUrl,
      artworkType: n.artworkType,
      frame: n.frame,
      background: n.background,
      caption: n.caption,
      seasonTheme: n.seasonTheme,
      network: n.network,
      hidden: n.hidden,
      createdAt: n.createdAt,
      explorerUrl: n.explorerUrl,
    })),
    total: r.total,
  };
}
